import { useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { Plus, Edit, Trash2, Eye, EyeOff, Upload, ChevronDown, ChevronRight } from 'lucide-react';
import type { Vendor, PINCode } from './VendorsScreen';
import type { SupervisorAccess, MicrosoftAccount } from './PINCodesScreen';
import type { Project } from '../../types/project';
import type { Operator } from '../../types/timeTracking';

const DEFAULT_PIN = '1234';
const DEFAULT_HOURS_PER_WEEK = 40;

type AccessTab = 'vendors' | 'operators' | 'supervisors';

interface ManageAccessScreenProps {
  vendors: Vendor[];
  pinCodes: PINCode[];
  projects: Project[];
  supervisorAccess: SupervisorAccess | null;
  microsoftAccounts: MicrosoftAccount[];
  operators: Operator[];
  onAddVendor: (vendor: Vendor) => void;
  onUpdateVendor: (vendor: Vendor) => void;
  onDeleteVendor: (vendorId: string) => void;
  onAddPINCode: (pinCode: PINCode) => void;
  onUpdatePINCode: (pinCode: PINCode) => void;
  onDeletePINCode: (pinCodeId: string) => void;
  onTogglePINStatus: (pinCodeId: string) => void;
  onUpdateSupervisorAccess: (access: SupervisorAccess) => void;
  onAddMicrosoftAccount: (account: MicrosoftAccount) => void;
  onUpdateMicrosoftAccount: (account: MicrosoftAccount) => void;
  onDeleteMicrosoftAccount: (accountId: string) => void;
  onAddOperator: (operator: Operator) => void;
  onAddOperators: (operators: Operator[]) => void;
  onUpdateOperator: (operator: Operator) => void;
  onDeleteOperator: (operatorId: string) => void;
}

// ─── VENDORS TAB ─────────────────────────────────────────────────────────────

function VendorsTab({
  vendors, pinCodes, projects,
  onAddVendor, onUpdateVendor, onDeleteVendor,
  onAddPINCode, onUpdatePINCode, onDeletePINCode,
}: Pick<ManageAccessScreenProps,
  'vendors' | 'pinCodes' | 'projects' |
  'onAddVendor' | 'onUpdateVendor' | 'onDeleteVendor' |
  'onAddPINCode' | 'onUpdatePINCode' | 'onDeletePINCode'
>) {
  const [isAddingVendor, setIsAddingVendor] = useState(false);
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);
  const [vendorName, setVendorName] = useState('');
  const [expandedVendors, setExpandedVendors] = useState<Set<string>>(new Set());
  const [addingPINForVendor, setAddingPINForVendor] = useState<string | null>(null);
  const [editingPIN, setEditingPIN] = useState<PINCode | null>(null);
  const [pinCode, setPinCode] = useState('');
  const [pinProjects, setPinProjects] = useState<string[]>([]);
  const [showPins, setShowPins] = useState<{ [k: string]: boolean }>({});

  const toggleVendor = (id: string) =>
    setExpandedVendors(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const saveVendor = () => {
    if (!vendorName.trim()) { alert('Enter a vendor name'); return; }
    if (editingVendor) {
      onUpdateVendor({ ...editingVendor, name: vendorName.trim() });
    } else {
      onAddVendor({ id: `vendor-${Date.now()}`, name: vendorName.trim(), pinCodes: [] });
    }
    setVendorName(''); setEditingVendor(null); setIsAddingVendor(false);
  };

  const startEditVendor = (v: Vendor) => { setEditingVendor(v); setVendorName(v.name); setIsAddingVendor(true); };

  const savePIN = (vendorId: string) => {
    if (!pinCode.trim()) { alert('Enter a PIN code'); return; }
    if (pinProjects.length === 0) { alert('Select at least one project'); return; }
    const vendor = vendors.find(v => v.id === vendorId)!;
    const data: PINCode = {
      id: editingPIN?.id || `pin-${Date.now()}`,
      code: pinCode.trim(),
      vendorId,
      vendorName: vendor.name,
      isActive: editingPIN?.isActive ?? true,
      projects: pinProjects,
      roleType: 'vendor',
    };
    editingPIN ? onUpdatePINCode(data) : onAddPINCode(data);
    setPinCode(''); setPinProjects([]); setAddingPINForVendor(null); setEditingPIN(null);
  };

  const startEditPIN = (pin: PINCode) => {
    setEditingPIN(pin); setPinCode(pin.code); setPinProjects(pin.projects || []);
    setAddingPINForVendor(pin.vendorId);
    setExpandedVendors(prev => new Set(prev).add(pin.vendorId));
  };

  const cancelPIN = () => { setPinCode(''); setPinProjects([]); setAddingPINForVendor(null); setEditingPIN(null); };

  const togglePinProject = (id: string) =>
    setPinProjects(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-600">Create vendors and assign PIN codes. Each PIN code can be restricted to specific projects.</p>

      {/* Add / Edit Vendor form */}
      {isAddingVendor ? (
        <div className="bg-white rounded-lg border border-gray-200 p-5 space-y-4">
          <h3 className="font-semibold text-gray-900">{editingVendor ? 'Edit Vendor' : 'New Vendor'}</h3>
          <input
            type="text"
            value={vendorName}
            onChange={e => setVendorName(e.target.value)}
            placeholder="Vendor name"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
          />
          <div className="flex gap-3">
            <button onClick={saveVendor} className="px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium">
              {editingVendor ? 'Update' : 'Create Vendor'}
            </button>
            <button onClick={() => { setIsAddingVendor(false); setEditingVendor(null); setVendorName(''); }} className="px-5 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button onClick={() => setIsAddingVendor(true)} className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium">
          <Plus className="w-4 h-4" /> Add Vendor
        </button>
      )}

      {/* Vendor list */}
      {vendors.length === 0 ? (
        <div className="text-center py-10 text-gray-500 bg-white rounded-lg border border-gray-200">No vendors yet.</div>
      ) : (
        <div className="space-y-3">
          {vendors.map(vendor => {
            const vPins = pinCodes.filter(p => p.vendorId === vendor.id);
            const expanded = expandedVendors.has(vendor.id);
            const addingHere = addingPINForVendor === vendor.id;

            return (
              <div key={vendor.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                {/* Vendor header */}
                <div className="flex items-center justify-between px-5 py-4">
                  <button onClick={() => toggleVendor(vendor.id)} className="flex items-center gap-2 text-left flex-1">
                    {expanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                    <span className="font-semibold text-gray-900">{vendor.name}</span>
                    <span className="text-sm text-gray-500">({vPins.length} PIN{vPins.length !== 1 ? 's' : ''})</span>
                  </button>
                  <div className="flex items-center gap-2">
                    <button onClick={() => startEditVendor(vendor)} className="px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg flex items-center gap-1">
                      <Edit className="w-3.5 h-3.5" /> Edit
                    </button>
                    <button onClick={() => { if (window.confirm('Delete this vendor and all their PIN codes?')) onDeleteVendor(vendor.id); }} className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-1">
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  </div>
                </div>

                {expanded && (
                  <div className="border-t border-gray-100 px-5 py-4 space-y-3">
                    {/* PIN list */}
                    {vPins.length === 0 && !addingHere && (
                      <p className="text-sm text-gray-400">No PIN codes yet.</p>
                    )}
                    {vPins.map(pin => (
                      <div key={pin.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-1">
                            <span className="font-mono text-sm font-semibold text-gray-900">
                              {showPins[pin.id] ? pin.code : '••••••'}
                            </span>
                            <button onClick={() => setShowPins(p => ({ ...p, [pin.id]: !p[pin.id] }))} className="text-gray-400 hover:text-gray-600">
                              {showPins[pin.id] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                            </button>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {(pin.projects || []).map(pid => {
                              const p = projects.find(x => x.id === pid);
                              return p ? <span key={pid} className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">{p.name}</span> : null;
                            })}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => startEditPIN(pin)} className="px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded flex items-center gap-1">
                            <Edit className="w-3 h-3" /> Edit
                          </button>
                          <button onClick={() => { if (window.confirm('Delete this PIN code?')) onDeletePINCode(pin.id); }} className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded flex items-center gap-1">
                            <Trash2 className="w-3 h-3" /> Delete
                          </button>
                        </div>
                      </div>
                    ))}

                    {/* Add/Edit PIN form */}
                    {addingHere ? (
                      <div className="bg-gray-50 rounded-lg p-4 space-y-3 mt-2">
                        <h4 className="text-sm font-semibold text-gray-700">{editingPIN ? 'Edit PIN Code' : 'New PIN Code'}</h4>
                        <input
                          type="text"
                          value={pinCode}
                          onChange={e => setPinCode(e.target.value)}
                          placeholder="PIN code"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
                        />
                        <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-xs font-medium text-gray-700">Access to Projects</span>
                            <div className="flex gap-2">
                              <button onClick={() => setPinProjects(projects.map(p => p.id))} className="text-xs text-indigo-600 hover:underline">All</button>
                              <span className="text-gray-300">|</span>
                              <button onClick={() => setPinProjects([])} className="text-xs text-gray-600 hover:underline">None</button>
                            </div>
                          </div>
                          <div className="border border-gray-200 rounded-lg max-h-36 overflow-y-auto">
                            {projects.length === 0 ? (
                              <p className="p-3 text-xs text-gray-400">No projects available.</p>
                            ) : projects.map(p => (
                              <label key={p.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                                <input type="checkbox" checked={pinProjects.includes(p.id)} onChange={() => togglePinProject(p.id)} className="w-3.5 h-3.5 text-indigo-600 border-gray-300 rounded" />
                                <span className="text-xs text-gray-700">{p.name}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => savePIN(vendor.id)} className="px-4 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 font-medium">
                            {editingPIN ? 'Update' : 'Add PIN'}
                          </button>
                          <button onClick={cancelPIN} className="px-4 py-1.5 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300 font-medium">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <button onClick={() => { setAddingPINForVendor(vendor.id); setPinCode(''); setPinProjects([]); setEditingPIN(null); }} className="flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-700 font-medium mt-1">
                        <Plus className="w-3.5 h-3.5" /> Add PIN Code
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── OPERATORS TAB ───────────────────────────────────────────────────────────

function OperatorsTab({
  operators, projects,
  onAddOperator, onAddOperators, onUpdateOperator, onDeleteOperator,
}: Pick<ManageAccessScreenProps,
  'operators' | 'projects' |
  'onAddOperator' | 'onAddOperators' | 'onUpdateOperator' | 'onDeleteOperator'
>) {
  const [isAdding, setIsAdding] = useState(false);
  const [editingOp, setEditingOp] = useState<Operator | null>(null);
  const [email, setEmail] = useState('');
  const [pin, setPin] = useState(DEFAULT_PIN);
  const [showPin, setShowPin] = useState(false);
  const [hoursPerWeek, setHoursPerWeek] = useState(String(DEFAULT_HOURS_PER_WEEK));
  const [allFuture, setAllFuture] = useState(true);
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const [showPins, setShowPins] = useState<{ [k: string]: boolean }>({});

  // Bulk import state
  const fileRef = useRef<HTMLInputElement>(null);
  const [importPreview, setImportPreview] = useState<string[] | null>(null);
  const [importPin, setImportPin] = useState(DEFAULT_PIN);

  const resetForm = () => {
    setIsAdding(false); setEditingOp(null); setEmail(''); setPin(DEFAULT_PIN);
    setShowPin(false); setHoursPerWeek(String(DEFAULT_HOURS_PER_WEEK));
    setAllFuture(true); setSelectedProjects([]);
  };

  const startEdit = (op: Operator) => {
    setEditingOp(op); setEmail(op.email); setPin(op.pin);
    setHoursPerWeek(String(op.regularHoursPerWeek));
    setAllFuture(op.allFutureProjects);
    setSelectedProjects(op.projectIds || []);
    setIsAdding(true);
  };

  const handleSave = () => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) { alert('Enter an email address.'); return; }
    if (!pin.trim()) { alert('Enter a PIN code.'); return; }
    const parsedHours = Number(hoursPerWeek);
    if (!Number.isFinite(parsedHours) || parsedHours <= 0) { alert('Enter a valid hours per week value.'); return; }
    if (!allFuture && selectedProjects.length === 0) { alert('Select at least one project or enable All Future Projects.'); return; }

    const dup = operators.find(op => op.email === normalizedEmail && op.id !== editingOp?.id);
    if (dup) { alert('An operator with this email already exists.'); return; }

    const data: Operator = {
      id: editingOp?.id || `operator-${Date.now()}`,
      email: normalizedEmail,
      pin: pin.trim(),
      isActive: editingOp?.isActive ?? true,
      regularHoursPerWeek: parsedHours,
      allFutureProjects: allFuture,
      projectIds: allFuture ? [] : selectedProjects,
    };

    editingOp ? onUpdateOperator(data) : onAddOperator(data);
    resetForm();
  };

  const toggleProject = (id: string) =>
    setSelectedProjects(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  // CSV / Excel import
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target?.result, { type: 'array' });
        const rows = XLSX.utils.sheet_to_json<string[]>(wb.Sheets[wb.SheetNames[0]], { header: 1 });
        const emails = rows
          .map(r => String(r[0] ?? '').trim().toLowerCase())
          .filter(e => e && e.includes('@') && e !== 'email');
        if (emails.length === 0) { alert('No valid email addresses found in the file.'); return; }
        setImportPreview(emails);
        setImportPin(DEFAULT_PIN);
      } catch {
        alert('Could not read the file. Please use a CSV or Excel file with emails in the first column.');
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const confirmImport = () => {
    if (!importPreview) return;
    const trimmedPin = importPin.trim();
    if (!trimmedPin) { alert('Enter a default PIN for the imported operators.'); return; }
    const existing = new Set(operators.map(op => op.email));
    const newOps: Operator[] = importPreview
      .filter(em => !existing.has(em))
      .map(em => ({
        id: `operator-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
        email: em,
        pin: trimmedPin,
        isActive: true,
        regularHoursPerWeek: DEFAULT_HOURS_PER_WEEK,
        allFutureProjects: true,
        projectIds: [],
      }));
    if (newOps.length === 0) { alert('All emails in the file are already added.'); setImportPreview(null); return; }
    onAddOperators(newOps);
    setImportPreview(null);
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-600">
        Operators log in with their email and PIN code. By default they get access to all projects (current and future).
      </p>

      {/* Import preview modal */}
      {importPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Import Preview</h3>
            <p className="text-sm text-gray-600">{importPreview.length} email address{importPreview.length !== 1 ? 'es' : ''} found.</p>
            <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto text-sm divide-y divide-gray-100">
              {importPreview.map(em => (
                <div key={em} className="px-3 py-2 text-gray-700">{em}</div>
              ))}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Default PIN for all imported operators</label>
              <input
                type="text"
                value={importPin}
                onChange={e => setImportPin(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
                placeholder="e.g. 1234"
              />
            </div>
            <div className="flex gap-3">
              <button onClick={confirmImport} className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium text-sm">
                Import {importPreview.length} Operator{importPreview.length !== 1 ? 's' : ''}
              </button>
              <button onClick={() => setImportPreview(null)} className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium text-sm">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-3 flex-wrap">
        {!isAdding && (
          <>
            <button onClick={() => { resetForm(); setIsAdding(true); }} className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium">
              <Plus className="w-4 h-4" /> Add Operator
            </button>
            <button onClick={() => fileRef.current?.click()} className="flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium">
              <Upload className="w-4 h-4" /> Import from CSV / Excel
            </button>
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={handleFileChange} />
          </>
        )}
      </div>

      {/* Format hint */}
      {!isAdding && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 space-y-1">
          <p className="font-semibold">CSV / Excel format:</p>
          <p>Put email addresses in the first column. You can optionally add "email" as a header in row 1 — it will be skipped automatically.</p>
          <p className="font-mono bg-amber-100 px-2 py-1 rounded">email<br />john@company.com<br />jane@company.com</p>
        </div>
      )}

      {/* Add / Edit form */}
      {isAdding && (
        <div className="bg-white rounded-lg border border-gray-200 p-5 space-y-4">
          <h3 className="font-semibold text-gray-900">{editingOp ? 'Edit Operator' : 'Add Operator'}</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                disabled={!!editingOp}
                placeholder="operator@company.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none disabled:bg-gray-50 disabled:text-gray-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">PIN Code</label>
              <div className="relative">
                <input
                  type={showPin ? 'text' : 'password'}
                  value={pin}
                  onChange={e => setPin(e.target.value)}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                />
                <button type="button" onClick={() => setShowPin(!showPin)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showPin ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>

          <div className="max-w-xs">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Regular Hours / Week</label>
            <input
              type="number"
              min="1"
              step="1"
              value={hoursPerWeek}
              onChange={e => setHoursPerWeek(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
            />
          </div>

          {/* Project access */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">Project Access</label>

            <label className="flex items-center gap-3 p-3 rounded-lg border border-indigo-200 bg-indigo-50 cursor-pointer">
              <input
                type="checkbox"
                checked={allFuture}
                onChange={e => {
                  setAllFuture(e.target.checked);
                  if (e.target.checked) setSelectedProjects([]);
                  else setSelectedProjects(projects.map(p => p.id));
                }}
                className="w-4 h-4 text-indigo-600 border-gray-300 rounded"
              />
              <div>
                <div className="text-sm font-medium text-indigo-900">All projects — current & future</div>
                <div className="text-xs text-indigo-700 mt-0.5">Operator automatically gets access to every project, including ones created later</div>
              </div>
            </label>

            {!allFuture && (
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-medium text-gray-700">Select specific projects</span>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setSelectedProjects(projects.map(p => p.id))} className="text-xs text-indigo-600 hover:underline">All</button>
                    <span className="text-gray-300">|</span>
                    <button type="button" onClick={() => setSelectedProjects([])} className="text-xs text-gray-600 hover:underline">None</button>
                  </div>
                </div>
                <div className="border border-gray-200 rounded-lg max-h-44 overflow-y-auto">
                  {projects.length === 0 ? (
                    <p className="p-3 text-sm text-gray-400">No projects created yet.</p>
                  ) : projects.map(p => (
                    <label key={p.id} className="flex items-center gap-2 px-3 py-2.5 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-0">
                      <input type="checkbox" checked={selectedProjects.includes(p.id)} onChange={() => toggleProject(p.id)} className="w-4 h-4 text-indigo-600 border-gray-300 rounded" />
                      <span className="text-sm text-gray-700">{p.name}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">{selectedProjects.length} of {projects.length} projects selected</p>
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-1">
            <button onClick={handleSave} className="px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium">
              {editingOp ? 'Update Operator' : 'Add Operator'}
            </button>
            <button onClick={resetForm} className="px-5 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium">Cancel</button>
          </div>
        </div>
      )}

      {/* Operator list */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="font-semibold text-gray-900">Operator List</h3>
          <p className="text-sm text-gray-500 mt-0.5">{operators.length} operator{operators.length !== 1 ? 's' : ''}</p>
        </div>
        {operators.length === 0 ? (
          <div className="py-10 text-center text-gray-500">No operators added yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Email</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">PIN</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Hrs/Week</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Projects</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {operators.map(op => (
                  <tr key={op.id} className="hover:bg-gray-50 transition">
                    <td className="px-5 py-3 text-sm text-gray-900 font-medium">{op.email}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-sm">{showPins[op.id] ? op.pin : '••••'}</span>
                        <button onClick={() => setShowPins(p => ({ ...p, [op.id]: !p[op.id] }))} className="text-gray-400 hover:text-gray-600">
                          {showPins[op.id] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-sm text-gray-700">{op.regularHoursPerWeek} hrs</td>
                    <td className="px-5 py-3">
                      {op.allFutureProjects ? (
                        <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded font-medium">All (incl. future)</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {(op.projectIds || []).map(pid => {
                            const p = projects.find(x => x.id === pid);
                            return p ? <span key={pid} className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">{p.name}</span> : null;
                          })}
                          {(op.projectIds || []).length === 0 && <span className="text-xs text-gray-400 italic">No projects</span>}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => startEdit(op)} className="px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg flex items-center gap-1">
                          <Edit className="w-3.5 h-3.5" /> Edit
                        </button>
                        <button onClick={() => { if (window.confirm('Remove this operator?')) onDeleteOperator(op.id); }} className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-1">
                          <Trash2 className="w-3.5 h-3.5" /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── SUPERVISORS TAB ─────────────────────────────────────────────────────────

function SupervisorsTab({
  microsoftAccounts, projects,
  onAddMicrosoftAccount, onUpdateMicrosoftAccount, onDeleteMicrosoftAccount,
}: Pick<ManageAccessScreenProps,
  'microsoftAccounts' | 'projects' |
  'onAddMicrosoftAccount' | 'onUpdateMicrosoftAccount' | 'onDeleteMicrosoftAccount'
>) {
  const [isAdding, setIsAdding] = useState(false);
  const [editingAccount, setEditingAccount] = useState<MicrosoftAccount | null>(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [hoursPerWeek, setHoursPerWeek] = useState(String(DEFAULT_HOURS_PER_WEEK));
  const [allFuture, setAllFuture] = useState(true);
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);

  const resetForm = () => {
    setIsAdding(false); setEditingAccount(null); setEmail(''); setName('');
    setHoursPerWeek(String(DEFAULT_HOURS_PER_WEEK)); setAllFuture(true); setSelectedProjects([]);
  };

  const startEdit = (account: MicrosoftAccount) => {
    setEditingAccount(account); setEmail(account.email); setName(account.name || '');
    setHoursPerWeek(String(account.regularHoursPerWeek ?? DEFAULT_HOURS_PER_WEEK));
    setAllFuture(account.allFutureProjects ?? false);
    setSelectedProjects(account.projects || []); setIsAdding(true);
  };

  const handleSave = () => {
    if (!email.trim()) { alert('Enter an email address.'); return; }
    if (!allFuture && selectedProjects.length === 0) { alert('Select at least one project or enable All Future Projects.'); return; }
    const parsedHours = Number(hoursPerWeek);
    if (!Number.isFinite(parsedHours) || parsedHours <= 0) { alert('Enter a valid hours per week value.'); return; }
    const data: MicrosoftAccount = {
      id: editingAccount?.id || `msaccount-${Date.now()}`,
      email: email.trim().toLowerCase(),
      name: name.trim() || undefined,
      isActive: editingAccount?.isActive ?? true,
      projects: allFuture ? [] : selectedProjects,
      regularHoursPerWeek: parsedHours,
      allFutureProjects: allFuture,
    };
    editingAccount ? onUpdateMicrosoftAccount(data) : onAddMicrosoftAccount(data);
    resetForm();
  };

  const toggleProject = (id: string) =>
    setSelectedProjects(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-600">Supervisors sign in with their Microsoft work account. Only approved email addresses listed here will be allowed in.</p>

      {isAdding ? (
        <div className="bg-white rounded-lg border border-gray-200 p-5 space-y-4">
          <h3 className="font-semibold text-gray-900">{editingAccount ? 'Edit Supervisor' : 'Add Supervisor'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email (Microsoft account)</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="supervisor@company.com" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Name (optional)</label>
              <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Display name" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
            </div>
          </div>
          <div className="max-w-xs">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Regular Hours / Week</label>
            <input
              type="number"
              min="1"
              step="1"
              value={hoursPerWeek}
              onChange={e => setHoursPerWeek(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
            />
          </div>
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
            Supervisors will sign in with their real Microsoft account. Only approved emails will be allowed.
          </div>
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">Project Access</label>

            <label className="flex items-center gap-3 p-3 rounded-lg border border-indigo-200 bg-indigo-50 cursor-pointer">
              <input
                type="checkbox"
                checked={allFuture}
                onChange={e => {
                  setAllFuture(e.target.checked);
                  if (e.target.checked) setSelectedProjects([]);
                  else setSelectedProjects(projects.map(p => p.id));
                }}
                className="w-4 h-4 text-indigo-600 border-gray-300 rounded"
              />
              <div>
                <div className="text-sm font-medium text-indigo-900">All projects — current & future</div>
                <div className="text-xs text-indigo-700 mt-0.5">Supervisor automatically gets access to every project, including ones created later</div>
              </div>
            </label>

            {!allFuture && (
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-medium text-gray-700">Select specific projects</span>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setSelectedProjects(projects.map(p => p.id))} className="text-xs text-indigo-600 hover:underline">All</button>
                    <span className="text-gray-300">|</span>
                    <button type="button" onClick={() => setSelectedProjects([])} className="text-xs text-gray-600 hover:underline">None</button>
                  </div>
                </div>
                <div className="border border-gray-200 rounded-lg max-h-44 overflow-y-auto">
                  {projects.length === 0 ? (
                    <p className="p-3 text-sm text-gray-400">No projects available.</p>
                  ) : projects.map(p => (
                    <label key={p.id} className="flex items-center gap-2 px-3 py-2.5 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-0">
                      <input type="checkbox" checked={selectedProjects.includes(p.id)} onChange={() => toggleProject(p.id)} className="w-4 h-4 text-indigo-600 border-gray-300 rounded" />
                      <span className="text-sm text-gray-700">{p.name}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">{selectedProjects.length} of {projects.length} projects selected</p>
              </div>
            )}
          </div>
          <div className="flex gap-3 pt-1">
            <button onClick={handleSave} className="px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium">
              {editingAccount ? 'Update Supervisor' : 'Add Supervisor'}
            </button>
            <button onClick={resetForm} className="px-5 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium">Cancel</button>
          </div>
        </div>
      ) : (
        <button onClick={() => { resetForm(); setIsAdding(true); }} className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium">
          <Plus className="w-4 h-4" /> Add Supervisor
        </button>
      )}

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="font-semibold text-gray-900">Supervisor Access List</h3>
          <p className="text-sm text-gray-500 mt-0.5">{microsoftAccounts.length} supervisor{microsoftAccounts.length !== 1 ? 's' : ''}</p>
        </div>
        {microsoftAccounts.length === 0 ? (
          <div className="py-10 text-center text-gray-500">No supervisors added yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Name</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Microsoft Account</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Hrs/Week</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Project Access</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {microsoftAccounts.map(account => (
                  <tr key={account.id} className="hover:bg-gray-50 transition">
                    <td className="px-5 py-3">
                      <span className="px-2 py-1 bg-indigo-100 text-indigo-700 text-sm rounded font-semibold">{account.name || 'Supervisor'}</span>
                    </td>
                    <td className="px-5 py-3 text-sm text-gray-700">{account.email}</td>
                    <td className="px-5 py-3 text-sm text-gray-700">{account.regularHoursPerWeek ?? DEFAULT_HOURS_PER_WEEK} hrs</td>
                    <td className="px-5 py-3">
                      {account.allFutureProjects ? (
                        <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded font-medium">All (incl. future)</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {(account.projects || []).map(pid => {
                            const p = projects.find(x => x.id === pid);
                            return p ? <span key={pid} className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">{p.name}</span> : null;
                          })}
                          {(account.projects || []).length === 0 && <span className="text-xs text-gray-400 italic">No projects</span>}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => startEdit(account)} className="px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg flex items-center gap-1">
                          <Edit className="w-3.5 h-3.5" /> Edit
                        </button>
                        <button onClick={() => { if (window.confirm('Remove this supervisor?')) onDeleteMicrosoftAccount(account.id); }} className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-1">
                          <Trash2 className="w-3.5 h-3.5" /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── MANAGE ACCESS SCREEN ─────────────────────────────────────────────────────

export function ManageAccessScreen(props: ManageAccessScreenProps) {
  const [activeTab, setActiveTab] = useState<AccessTab>('vendors');

  const tabs: { id: AccessTab; label: string }[] = [
    { id: 'vendors', label: 'Vendors' },
    { id: 'operators', label: 'Operators' },
    { id: 'supervisors', label: 'Supervisors' },
  ];

  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Manage Access</h2>
        <p className="text-gray-600 mt-1">Control who has access to the system and which projects they can see.</p>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition ${
              activeTab === tab.id
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'vendors' && (
        <VendorsTab
          vendors={props.vendors}
          pinCodes={props.pinCodes}
          projects={props.projects}
          onAddVendor={props.onAddVendor}
          onUpdateVendor={props.onUpdateVendor}
          onDeleteVendor={props.onDeleteVendor}
          onAddPINCode={props.onAddPINCode}
          onUpdatePINCode={props.onUpdatePINCode}
          onDeletePINCode={props.onDeletePINCode}
        />
      )}
      {activeTab === 'operators' && (
        <OperatorsTab
          operators={props.operators}
          projects={props.projects}
          onAddOperator={props.onAddOperator}
          onAddOperators={props.onAddOperators}
          onUpdateOperator={props.onUpdateOperator}
          onDeleteOperator={props.onDeleteOperator}
        />
      )}
      {activeTab === 'supervisors' && (
        <SupervisorsTab
          microsoftAccounts={props.microsoftAccounts}
          projects={props.projects}
          onAddMicrosoftAccount={props.onAddMicrosoftAccount}
          onUpdateMicrosoftAccount={props.onUpdateMicrosoftAccount}
          onDeleteMicrosoftAccount={props.onDeleteMicrosoftAccount}
        />
      )}
    </div>
  );
}
